import React, { useState } from 'react';
import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { integrationApiService, ShopifyCustomAppSummary } from '../services/integrationApi';
import { useAppTranslation } from '../i18n/useAppTranslation';
import { useToast } from '../contexts/ToastContext';
import { ConfirmDialog, FormDialog, SectionCard, StatusChip } from './ui';
import { CUSTOM_APP_DRAFTS_QUERY_KEY } from './shopifyCustomAppWizard/ShopifyCustomAppWizard';
import {
  CUSTOM_APP_STEP_COUNT,
  CUSTOM_APP_STEP_IDS,
  resolveStoredDone,
  resolveStoredStep,
} from './shopifyCustomAppWizard/customAppWizardModel';

interface Props {
  /** Removing a saved app or changing its install link is a credential write: owner/admin only. */
  canManage: boolean;
  onContinue: (shopId: string) => void;
  /** Open the custom app setup wizard on the step this store stopped at. */
  onContinueSetup: (shopId: string) => void;
}

/**
 * Store-owned Shopify apps saved in Motzu whose install has not finished, so it can be picked up
 * without re-entering the Client ID and secret. Shopify installs a custom-distribution app the
 * first time only from its install link, so until Shopify has opened Motzu for the app the card
 * offers that link instead of Continue install. A store part way through the setup wizard is listed
 * too, so "you can close this and come back" has somewhere to come back to. Renders nothing when
 * there are none.
 */
const ShopifyPendingCustomApps: React.FC<Props> = ({ canManage, onContinue, onContinueSetup }) => {
  const { t } = useAppTranslation();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [removeTarget, setRemoveTarget] = useState<ShopifyCustomAppSummary | null>(null);
  const [linkTarget, setLinkTarget] = useState<ShopifyCustomAppSummary | null>(null);
  const [linkDraft, setLinkDraft] = useState('');

  const { data } = useQuery({
    queryKey: ['shopify-custom-apps'],
    queryFn: () => integrationApiService.getShopifyCustomApps(),
    staleTime: 30_000,
  });

  const { data: drafts } = useQuery({
    queryKey: CUSTOM_APP_DRAFTS_QUERY_KEY,
    queryFn: () => integrationApiService.getShopifyCustomAppDrafts(),
    staleTime: 30_000,
    retry: false,
  });

  const removeDraftMutation = useMutation({
    mutationFn: (shopId: string) => integrationApiService.deleteShopifyCustomAppDraft(shopId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CUSTOM_APP_DRAFTS_QUERY_KEY });
      showToast(t('integrations.customAppWizard.installer.draftRemoved'), 'success');
    },
    onError: (error) =>
      showToast((error as Error)?.message || t('integrations.customAppWizard.installer.draftRemoveFailed'), 'error'),
  });

  const removeMutation = useMutation({
    mutationFn: (shopId: string) => integrationApiService.deleteShopifyCustomApp(shopId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shopify-custom-apps'] });
      showToast(t('integrations.customAppResume.removed'), 'success');
      setRemoveTarget(null);
    },
    onError: (error) =>
      showToast((error as Error)?.message || t('integrations.customAppResume.removeFailed'), 'error'),
  });

  const linkMutation = useMutation({
    mutationFn: ({ shopId, link }: { shopId: string; link: string }) =>
      integrationApiService.setShopifyCustomAppInstallLink(shopId, link),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shopify-custom-apps'] });
      showToast(t('integrations.customAppResume.linkSaved'), 'success');
      setLinkTarget(null);
    },
    onError: (error) =>
      showToast((error as Error)?.message || t('integrations.customAppResume.linkSaveFailed'), 'error'),
  });

  const openLinkDialog = (app: ShopifyCustomAppSummary) => {
    setLinkDraft(app.installLink || '');
    setLinkTarget(app);
  };

  const copyLink = async (link: string) => {
    try {
      await navigator.clipboard.writeText(link);
      showToast(t('integrations.customAppResume.linkCopied'), 'success');
    } catch {
      showToast(t('integrations.customAppWizard.copyFailed'), 'error');
    }
  };

  const pending = (data || []).filter((app) => app.status !== 'installed');
  const unfinished = (drafts || []).filter((draft) => !pending.some((app) => app.shopId === draft.shopId));
  if (pending.length === 0 && unfinished.length === 0) return null;

  return (
    <>
      <SectionCard
        dense
        title={t('integrations.customAppResume.cardTitle')}
        subtitle={t('integrations.customAppResume.cardSubtitle')}
        sx={{ mb: 2 }}
      >
        <Stack divider={<Box sx={{ borderTop: '1px solid', borderColor: 'divider' }} />}>
          {unfinished.map((draft) => (
            <Box
              key={draft.shopId}
              sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2, py: 1.5, flexWrap: 'wrap' }}
            >
              <Box sx={{ flex: '1 1 260px', minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle2">{draft.shopDomain}</Typography>
                  <StatusChip size="small" tone="caution" label={t('integrations.customAppWizard.installer.draftChip')} />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {t('integrations.customAppWizard.installer.draftLine', {
                    done: resolveStoredDone(draft.done).length,
                    total: CUSTOM_APP_STEP_COUNT,
                    step: CUSTOM_APP_STEP_IDS.indexOf(resolveStoredStep(draft.step)) + 1,
                  })}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  size="small"
                  color="inherit"
                  disabled={removeDraftMutation.isPending}
                  onClick={() => removeDraftMutation.mutate(draft.shopId)}
                >
                  {t('integrations.customAppResume.remove')}
                </Button>
                <Button size="small" variant="contained" onClick={() => onContinueSetup(draft.shopId)}>
                  {t('integrations.customAppWizard.installer.draftContinue')}
                </Button>
              </Box>
            </Box>
          ))}
          {pending.map((app) => (
            <Box
              key={app.id}
              sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2, py: 1.5, flexWrap: 'wrap' }}
            >
              <Box sx={{ flex: '1 1 260px', minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle2">{app.shopDomain}</Typography>
                  <StatusChip
                    size="small"
                    tone={app.status === 'failed' ? 'problem' : 'caution'}
                    label={
                      app.status === 'failed'
                        ? t('integrations.customAppResume.statusFailed')
                        : t('integrations.customAppResume.statusAwaiting')
                    }
                  />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {t('integrations.customAppResume.savedLine', {
                    clientId: app.clientId,
                    date: app.savedAt ? new Date(app.savedAt).toLocaleDateString() : '—',
                  })}
                </Typography>
                {app.needsInstallLink && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                    {t(
                      app.installLink
                        ? 'integrations.customAppResume.installLinkHint'
                        : 'integrations.customAppResume.installLinkMissing',
                      { shop: app.shopDomain },
                    )}
                  </Typography>
                )}
                {app.lastError && (
                  <Typography variant="body2" color="error.main" sx={{ mt: 0.25 }}>
                    {t('integrations.customAppResume.lastError', { message: app.lastError.message })}
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {canManage && (
                  <Button size="small" color="inherit" onClick={() => setRemoveTarget(app)}>
                    {t('integrations.customAppResume.remove')}
                  </Button>
                )}
                {app.needsInstallLink ? (
                  <>
                    {canManage && (
                      <Button
                        size="small"
                        color="inherit"
                        variant={app.installLink ? 'text' : 'contained'}
                        onClick={() => openLinkDialog(app)}
                      >
                        {t(
                          app.installLink
                            ? 'integrations.customAppResume.editLink'
                            : 'integrations.customAppResume.addLink',
                        )}
                      </Button>
                    )}
                    {app.installLink && (
                      <>
                        <Button size="small" onClick={() => void copyLink(app.installLink as string)}>
                          {t('integrations.customAppResume.copyLink')}
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          href={app.installLink}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {t('integrations.customAppResume.openLink')}
                        </Button>
                      </>
                    )}
                  </>
                ) : (
                  <Button size="small" variant="contained" onClick={() => onContinue(app.shopId)}>
                    {t('integrations.customAppResume.continue')}
                  </Button>
                )}
              </Box>
            </Box>
          ))}
        </Stack>
      </SectionCard>
      <FormDialog
        open={linkTarget !== null}
        title={t('integrations.customAppResume.linkDialogTitle')}
        submitLabel={t('integrations.customAppResume.linkDialogSave')}
        submitDisabled={!linkDraft.trim()}
        loading={linkMutation.isPending}
        onSubmit={() => linkTarget && linkMutation.mutate({ shopId: linkTarget.shopId, link: linkDraft })}
        onClose={() => setLinkTarget(null)}
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('integrations.customAppResume.linkDialogBody', { shop: linkTarget?.shopDomain || '' })}
        </Typography>
        <TextField
          label={t('integrations.customAppResume.linkField')}
          value={linkDraft}
          onChange={(event) => setLinkDraft(event.target.value)}
          placeholder="https://admin.shopify.com/…"
          fullWidth
          autoFocus
        />
      </FormDialog>
      <ConfirmDialog
        open={removeTarget !== null}
        title={t('integrations.customAppResume.removeTitle')}
        message={t('integrations.customAppResume.removeMessage', { shop: removeTarget?.shopDomain || '' })}
        confirmLabel={t('integrations.customAppResume.remove')}
        confirmColor="error"
        loading={removeMutation.isPending}
        onConfirm={() => removeTarget && removeMutation.mutate(removeTarget.shopId)}
        onClose={() => setRemoveTarget(null)}
      />
    </>
  );
};

export default ShopifyPendingCustomApps;
